import {
  ArrowRight,
  CheckSquare2,
  CreditCard,
  FileText,
  PackageCheck,
  ReceiptText,
  RefreshCw,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  DashboardActivityItem,
  DashboardApprovalItem,
  DashboardSummaryCard,
  DashboardSummaryCardCode,
} from '@/api/dashboard.api';
import { Button } from '@/components/ui/Button';
import { Card, PageHeader, SectionHeading } from '@/components/ui/Page';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useDashboardOverview } from '@/hooks/useDashboard';

const dateTime = (value: string) =>
  new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));

const relativeTime = (value: string) => {
  const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(value)) / 1000));
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const cardConfig: Partial<Record<DashboardSummaryCardCode, { icon: typeof CheckSquare2; to: string }>> = {
  PENDING_APPROVALS: { icon: CheckSquare2, to: '/app/my-approvals' },
  OPEN_PURCHASE_ORDERS: { icon: FileText, to: '/app/purchase-orders' },
  PENDING_GRNS: { icon: PackageCheck, to: '/app/goods-receipts' },
  PENDING_INVOICES: { icon: ReceiptText, to: '/app/invoices' },
  PENDING_PAYMENTS: { icon: CreditCard, to: '/app/payments' },
};

const cardUrl = (card: DashboardSummaryCard, baseUrl: string) => {
  if (!card.filterKey || !card.filterValues?.length) return baseUrl;
  const params = new URLSearchParams();
  if (card.filterKey === 'statuses') card.filterValues.forEach((value) => params.append('statuses', value));
  else params.set(card.filterKey, card.filterValues[0]);
  return `${baseUrl}?${params.toString()}`;
};

const severityTone = (severity: string) => {
  const value = severity.toLowerCase();
  if (['critical', 'error', 'danger', 'high'].includes(value)) return 'red';
  if (['warning', 'warn', 'medium'].includes(value)) return 'amber';
  if (['success', 'healthy', 'low'].includes(value)) return 'green';
  if (['info', 'informational'].includes(value)) return 'blue';
  return 'neutral';
};

const taskUrl = (task: DashboardApprovalItem) =>
  `/app/approvals?taskId=${encodeURIComponent(task.taskId)}&instanceId=${encodeURIComponent(task.instanceId)}`;

const activityUrl = (activity: DashboardActivityItem) =>
  activity.entityType === 'VENDOR'
    ? `/app/vendors/${encodeURIComponent(activity.entityId)}`
    : '/app/audit?period=DAY';

export function DashboardPage() {
  const overview = useDashboardOverview();
  const data = overview.data;
  const recentActivity = data
    ? [...(data.recentActivity ?? [])].sort((a, b) => Date.parse(b.performedAt) - Date.parse(a.performedAt)).slice(0, 8)
    : [];

  if (overview.isLoading) {
    return <><PageHeader title="Overview" description="Operational status across vendor management and procure-to-pay." /><Card><div className="admin-panel-state">Loading dashboard overview…</div></Card></>;
  }

  if (overview.isError || !data) {
    return <><PageHeader title="Overview" description="Operational status across vendor management and procure-to-pay." /><Card><div className="admin-dashboard-error"><strong>Dashboard unavailable</strong><span>The overview could not be loaded.</span><Button variant="secondary" onClick={() => overview.refetch()}><RefreshCw /> Try again</Button></div></Card></>;
  }

  return (
    <div className="admin-dashboard">
      <PageHeader
        className="admin-dashboard__header"
        title="Overview"
        description="Operational status and actions across vendor management and procure-to-pay."
      />

      <div className="admin-summary-grid">
        {(data.summaryCards ?? []).map((card) => {
          const config = cardConfig[card.code];
          if (!config) return null;
          const Icon = config.icon;
          return (
            <Link
              className={`admin-summary-card admin-summary-card--${severityTone(card.severity)}`}
              to={cardUrl(card, config.to)}
              key={card.code}
              title={card.statusLabels?.length ? `${card.label} — Includes: ${card.statusLabels.join(', ')}` : card.label}
            >
              <span><Icon /></span>
              <div>
                <strong>{card.value.toLocaleString('en-IN')}{card.unit ? <small className="admin-summary-card__unit">{card.unit}</small> : null}</strong>
                <small>{card.label}</small>
              </div>
              <ArrowRight />
            </Link>
          );
        })}
      </div>

      <div className="admin-dashboard-grid admin-dashboard-grid--queue admin-dashboard-grid--single">
        <Card className="admin-panel admin-pending-panel">
          <SectionHeading
            title="My pending approvals"
            description="Items assigned to your workflow roles."
            action={<Link className="text-link" to="/app/approvals">View all <ArrowRight size={14} /></Link>}
          />
          {!data.approvalQueue.length
            ? <div className="admin-panel-state">No pending approvals. You are all caught up.</div>
            : <div className="admin-table-scroll">
              <div className="admin-action-table admin-action-table--head">
                <span>Type</span><span>Document no.</span><span>Item</span><span>Stage</span><span>Assigned role</span><span>Created</span><span>Action</span>
              </div>
              {data.approvalQueue.slice(0, 10).map((task) => (
                <div className="admin-action-table" key={task.taskId}>
                  <span>{task.documentType.replaceAll('_', ' ')}</span>
                  <strong>{task.documentNo || '—'}</strong>
                  <span>{task.title}</span>
                  <span><StatusBadge status={`Step ${task.stepNo}`} dot={false} /></span>
                  <span>{task.assignedRoleCode.replaceAll('_', ' ')}</span>
                  <time title={dateTime(task.createdAt)}>{relativeTime(task.createdAt)}</time>
                  <Link className="button button--secondary button--sm" to={taskUrl(task)}>View</Link>
                </div>
              ))}
            </div>}
        </Card>

      </div>

      <Card className="admin-panel">
        <SectionHeading
          title="Recent activity"
          description="Latest business activity across the workspace."
          action={<Link className="text-link" to="/app/audit?period=DAY">View all <ArrowRight size={14} /></Link>}
        />
        {!recentActivity.length
          ? <div className="admin-panel-state">No recent business activity.</div>
          : <div className="admin-table-scroll">
            <div className="admin-activity-table admin-activity-table--head"><span>Time</span><span>Event</span><span>Summary</span><span>User</span><span>Linked record</span></div>
            {recentActivity.map((activity) => (
              <Link className="admin-activity-table" to={activityUrl(activity)} key={activity.id}>
                <time title={dateTime(activity.performedAt)}>{relativeTime(activity.performedAt)}</time>
                <strong>{activity.title}</strong>
                <span>{activity.summary || '—'}</span>
                <span>{activity.performedByName ?? 'System'}</span>
                <span>{activity.entityReference || '—'}</span>
              </Link>
            ))}
          </div>}
      </Card>

    </div>
  );
}
